import { Box, Button, Stack, StackProps, Typography } from '@mui/material'
import { FC, useId } from 'react'
import { FormType, ModeType } from '../EngagementDetailsEnums'

interface DropdownItemProps {
  children: React.ReactNode
  onClick: () => void
  icon?: React.ReactNode
  endIcon?: React.ReactNode
  'data-testid'?: string
}

const DropdownItem: FC<DropdownItemProps> = ({ children, onClick, icon, endIcon, 'data-testid': dataTestId }) => (
  <Button
    data-testid={dataTestId}
    role="menuitem"
    sx={{
      gap: 'var(--spacing-2xs)',
      padding: 'var(--spacing-2xs) var(--spacing-s)',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: 'none',
      borderRadius: 0,
      '&:hover': { backgroundColor: 'var(--button-primary-hover)' }
    }}
    onClick={onClick}
  >
    <Stack direction="row" sx={{ gap: 'var(--spacing-2xs)', alignItems: 'center' }}>
      {icon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>}
      <Typography sx={{ color: 'var(--text-primary)' }}>{children}</Typography>
    </Stack>
    {endIcon && <Box sx={{ display: 'flex', alignItems: 'center', color: 'var(--text-primary)' }}>{endIcon}</Box>}
  </Button>
)

type DropdownProps = Omit<StackProps, 'onSelect'> & {
  prompt: string
  options: (ModeType | FormType)[]
  onSelect: (option: ModeType | FormType) => void
  getIcon?: (option: ModeType | FormType) => React.ReactNode
  getEndIcon?: (option: ModeType | FormType) => React.ReactNode
  'data-testid'?: string
}

export const Dropdown: FC<DropdownProps> = ({ prompt, options, onSelect, getIcon, getEndIcon, 'data-testid': dataTestId, ...stackProps }) => {
  const promptId = `dropdown-prompt-${useId()}`
  const { sx = {}, ...restStackProps } = stackProps
  return (
    <Stack
      direction="column"
      data-testid={dataTestId}
      role="menu"
      aria-labelledby={promptId}
      sx={{
        position: 'absolute',
        width: '12.5rem',
        backgroundColor: 'var(--bg-surface)',
        border: '0.0625rem solid var(--bp-art-ert-info-box-border)',
        borderRadius: 'var(--spacing-2xs)',
        zIndex: 1000,
        boxShadow: '0px 4px 4px 0px #00000040',
        overflow: 'hidden',
        ...sx
      }}
      {...restStackProps}
    >
      <Box sx={{ height: '2rem', padding: 'var(--spacing-2xs) var(--spacing-2xs) 0 var(--spacing-s)', marginBlockStart: 'var(--spacing-3xs)' }}>
        <Typography id={promptId} sx={{ fontSize: 'var(--size-xs)', fontWeight: 500, color: 'var(--text-secondary)' }}>
          {prompt.toUpperCase()}
        </Typography>
      </Box>
      {options.length === 0 && (
        <Box sx={{ height: '3rem', padding: 'var(--spacing-s)', display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ color: 'var(--text-primary)' }}>No options available</Typography>
        </Box>
      )}
      {options.map(option => (
        <DropdownItem
          key={option}
          onClick={() => onSelect(option)}
          icon={getIcon ? getIcon(option) : undefined}
          endIcon={getEndIcon ? getEndIcon(option) : undefined}
          data-testid={`dropdown-item-${option.toLowerCase()}`}
        >
          {option}
        </DropdownItem>
      ))}
    </Stack>
  )
}
